import { css, cx } from "@emotion/css";
import { Callout } from "@octopusdeploy/design-system-components";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { DeploymentFreezeStatus, type DeploymentFreezeSummary, type PaginatedCollection, type DeploymentFreezeListPage } from "@octopusdeploy/octopus-server-client";
import React from "react";
import NumberedPagingBar from "~/components/PagingBaseComponent/NumberedPagingBar";
import { DataTable, DataTableBody, DataTableFooter, DataTableHeader, DataTableHeaderColumn, DataTableRow } from "~/primitiveComponents/dataDisplay/DataTable/index";
import { DeploymentFreezeDataRecurrenCadenceCell } from "./Cells/DeploymentFreezeDataRecurrenCadenceCell";
import { DeploymentFreezeDataTableEnvironmentsCell } from "./Cells/DeploymentFreezeDataTableEnvironmentsCell";
import { DeploymentFreezeDataTableNameCell } from "./Cells/DeploymentFreezeDataTableNameCell";
import { DeploymentFreezeDataTableScopesCell } from "./Cells/DeploymentFreezeDataTableScopesCell";
import { DeploymentFreezeDataTableStatusCell } from "./Cells/DeploymentFreezeDataTableStatusCell";
interface DeploymentFreezeTableContentProps {
    deploymentFreezes: PaginatedCollection<DeploymentFreezeSummary>;
    currentPage: number;
    showTenantScope: boolean;
    tenantScopeHeader: string;
    freezeListPage: DeploymentFreezeListPage;
    onPageSelected: (pageNumber: number) => void;
}
export function DeploymentFreezeTableContent({ deploymentFreezes, currentPage, showTenantScope, tenantScopeHeader, freezeListPage, onPageSelected }: DeploymentFreezeTableContentProps) {
    const getGroupStatusHeader = (status: DeploymentFreezeStatus, groupItemCount?: number) => (<DataTableRow key={`header-${status}`} className={groupHeaderStyles}>
            <DeploymentFreezeDataTableStatusCell totalItems={groupItemCount} colSpan={4} status={status}/>
        </DataTableRow>);
    const freezeStatusGroups = Object.values(DeploymentFreezeStatus).map((status) => ({
        key: status,
        selector: (item: DeploymentFreezeSummary) => item.Status === status,
    }));
    const groupedFreezeData = freezeStatusGroups.reduce((acc, group) => {
        const groupItems = deploymentFreezes.Items.filter(group.selector);
        acc[group.key] = {
            items: groupItems,
            header: getGroupStatusHeader(group.key, groupItems.length),
        };
        return acc;
    }, {
        [DeploymentFreezeStatus.Active]: createEmptyGroupedData(DeploymentFreezeStatus.Active),
        [DeploymentFreezeStatus.Scheduled]: createEmptyGroupedData(DeploymentFreezeStatus.Scheduled),
        [DeploymentFreezeStatus.Expired]: createEmptyGroupedData(DeploymentFreezeStatus.Expired),
        [DeploymentFreezeStatus.Unscoped]: createEmptyGroupedData(DeploymentFreezeStatus.Unscoped),
    });
    const unscopedFreezes = deploymentFreezes.Items.filter((f) => f.Status === DeploymentFreezeStatus.Unscoped);
    const unscopedCalloutRow = unscopedFreezes.length > 0 ? (<DataTableRow key="callout-Unscoped">
                <td colSpan={4} className={calloutCellStyles}>
                    <Callout type="information" title="Unscoped deployment freezes">
                        These deployment freezes are not currently blocking any deployments because they have no tenant or project scopes. Edit a freeze and add scopes to reactivate it.
                    </Callout>
                </td>
            </DataTableRow>) : null;
    const rows = Object.entries(groupedFreezeData)
        .flatMap(([status, { items: freezes, header }]) => [
        freezes.length > 0 && header,
        status === DeploymentFreezeStatus.Unscoped && unscopedCalloutRow,
        ...freezes.map((freeze) => {
            const hasMissingResource = freeze.HasMissingResourceDueToPermissions;
            return (<DataTableRow key={freeze.Id} disabled={hasMissingResource} className={hasMissingResource ? "freeze-disabled-row" : ""}>
                        <DeploymentFreezeDataTableNameCell freezeListPage={freezeListPage} freeze={freeze} hasMissingResource={hasMissingResource}/>
                        <DeploymentFreezeDataRecurrenCadenceCell recurringSchedule={freeze.RecurringSchedule} startDate={freeze.RecurringSchedule?.StartDate?.toString() || freeze.Start}/>
                        <DeploymentFreezeDataTableScopesCell freeze={freeze} freezeListPage={freezeListPage} showTenantScope={showTenantScope}/>
                        <DeploymentFreezeDataTableEnvironmentsCell freeze={freeze} hideTenantScope={!showTenantScope}/>
                    </DataTableRow>);
        }),
    ])
        .filter(Boolean);
    return (<DataTable className={cx(tableStyles)} title={"Deployment Freezes Overview"}>
            <DataTableHeader>
                <DataTableRow>
                    <DataTableHeaderColumn>Deployment Freezes</DataTableHeaderColumn>
                    <DataTableHeaderColumn>Recurrence Cadence</DataTableHeaderColumn>
                    {showTenantScope && <DataTableHeaderColumn>{tenantScopeHeader}</DataTableHeaderColumn>}
                    <DataTableHeaderColumn>Environments</DataTableHeaderColumn>
                </DataTableRow>
            </DataTableHeader>
            <DataTableBody>{rows}</DataTableBody>
            <DataTableFooter>
                <NumberedPagingBar currentPageIndex={currentPage} totalItems={deploymentFreezes.TotalResults} pageSize={deploymentFreezes.ItemsPerPage} onPageSelected={(_, currentPageIndex) => onPageSelected(currentPageIndex)}/>
            </DataTableFooter>
        </DataTable>);
    function createEmptyGroupedData(status: DeploymentFreezeStatus): {
        items: DeploymentFreezeSummary[];
        header: React.ReactElement;
    } {
        return {
            items: [],
            header: getGroupStatusHeader(status),
        };
    }
}
const tableBorder = `${borderWidth[1]} solid ${themeTokens.color.border.primary}`;
const tableStyles = css({
    width: "100%",
    backgroundColor: themeTokens.color.background.primary.default,
    border: tableBorder,
    borderRadius: borderRadius.large,
    borderSpacing: 0,
    th: {
        padding: `${space[12]} ${space[16]}`,
        paddingRight: 0,
        textAlign: "left",
        font: text.body.regular.medium,
        position: "relative",
        ":not(:last-child)": {
            ":after": {
                content: "''",
                position: "absolute",
                height: "50%",
                right: 0,
                top: "25%",
                borderRight: tableBorder,
            },
        },
    },
    td: {
        padding: `${space[16]} ${space[12]}`,
        borderTop: tableBorder,
        verticalAlign: "middle",
        "&.freeze-scope-cell": {
            paddingRight: 0,
        },
        "&.freeze-env-cell": {
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
    tr: {
        "&.freeze-disabled-row": {
            opacity: 0.7,
        },
    },
});
const groupHeaderStyles = css({
    backgroundColor: themeTokens.color.background.secondary.default,
    fontWeight: "bold",
    td: {
        padding: `${space[8]} ${space[12]}`,
    },
});
const calloutCellStyles = css({
    padding: `${space["16"]} ${space["12"]}`,
    borderTop: tableBorder,
    backgroundColor: themeTokens.color.background.primary.default,
});
